import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireClerkCoachAuth } from "~/lib/coach-middleware";
import { InvoiceDueDateSchema } from "~/lib/schemas/invoice";
import { isKnownTimeZone } from "~/lib/shared/invoice-due";

const CreateInvoiceDataSchema = z.object({
  customerName: z.string().min(1),
  customerEmail: z.string().email(),
  description: z.string().min(1),
  amount: z.number().int().positive(),
  currency: z.string().min(1),
  dueDate: InvoiceDueDateSchema,
  engagementId: z.string().uuid().optional(),
});

const CoachInvoiceSchema = z.object({
  stripeInvoiceId: z.string(),
  invoiceId: z.string().uuid(),
});

export const createInvoiceForCoachFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(CreateInvoiceDataSchema)
  .handler(async ({ data, context }) => {
    const { createInvoiceForCoach } = await import("~/lib/server/invoice");
    return createInvoiceForCoach(
      data.customerName,
      data.customerEmail,
      data.description,
      data.amount,
      data.currency,
      data.dueDate,
      context.coach,
      data.engagementId,
    );
  });

/**
 * How many of the signed-in coach's invoices are open after their due day.
 * The day ends on her clock, so the browser sends its time zone.
 */
export const getMyOverdueInvoiceCountFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .validator(
    z.object({
      timeZone: z.string().max(64).refine(isKnownTimeZone, {
        message: "Unknown time zone",
      }),
    }),
  )
  .handler(async ({ data, context }): Promise<number> => {
    const { countOverdueInvoices } = await import("~/lib/server/invoice");
    return countOverdueInvoices(context.coach.id, new Date(), data.timeZone);
  });

export const deleteInvoiceForCoachFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(CoachInvoiceSchema)
  .handler(async ({ data, context }) => {
    const { deleteCoachInvoice } = await import("~/lib/server/invoice");
    return deleteCoachInvoice({ ...data, coachId: context.coach.id });
  });

export const voidInvoiceForCoachFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(CoachInvoiceSchema)
  .handler(async ({ data, context }) => {
    const { voidCoachInvoice } = await import("~/lib/server/invoice");
    return voidCoachInvoice({ ...data, coachId: context.coach.id });
  });
